import { useState } from 'react';
import { Button, Descriptions } from '@arco-design/web-react';
import DetailSection from '../DetailSection';
import EmptyValue from '../EmptyValue';
import { ticketFieldDisplayValue, type Ticket, type VocabTreeNode } from '../../api/ticket';

export interface TicketFieldsPanelProps {
  /** 工单详情（field_defs / field_values 为业务字段数据源） */
  detail: Ticket;
  /** 工单分类树：cascade 字段把落库分类 code 还原为分类名（无该类型字段时为空数组） */
  categoryTree: VocabTreeNode[];
}

/**
 * 工单详情「业务字段」分节：动态字段由「工单字段配置」产出，数量随配置增长且常见大量留空，
 * 故与固定字段分开成节，并对空值做折叠（空值是否展示是本分片内部状态，不外泄）。
 */
export default function TicketFieldsPanel({ detail, categoryTree }: TicketFieldsPanelProps) {
  // 业务字段的空值折叠：动态字段常有十几个、其中大半未填（实测一条工单 24 个字段里 15 个为空），
  // 默认只展示有值的，空值收在「展开其余 N 项」后面，避免整屏横杠把有效信息淹掉
  const [showEmptyFields, setShowEmptyFields] = useState(false);

  // 动态业务字段：把「有值 / 空值」先分开，空值是否展示由 showEmptyFields 决定（默认收起）；
  // 落库的是选项 value / 分类 code，展示统一走 ticketFieldDisplayValue 还原中文（与表单下拉一致）
  const dynamicFields = (detail.field_defs || []).map((f) => {
    const raw = (detail.field_values || {})[f.field_key];
    const filled = !(raw === null || raw === undefined || raw === '');
    return {
      label: f.label,
      filled,
      value: filled ? ticketFieldDisplayValue(f, raw, categoryTree) : <EmptyValue />,
    };
  });
  const emptyFieldCount = dynamicFields.filter((f) => !f.filled).length;

  if (dynamicFields.length === 0) return null;
  return (
    /* 动态业务字段单独成节：字段由「工单字段配置」产出，数量随配置增长且常见大量留空，
       故与固定字段分开，并对空值做折叠 */
    <DetailSection
      title="业务字段"
      extra={
        emptyFieldCount > 0 ? (
          <Button type="text" size="small" onClick={() => setShowEmptyFields((v) => !v)}>
            {showEmptyFields ? '仅看已填写' : `展开其余 ${emptyFieldCount} 项`}
          </Button>
        ) : undefined
      }
    >
      <Descriptions
        column={2}
        data={(showEmptyFields ? dynamicFields : dynamicFields.filter((f) => f.filled)).map((f) => ({
          label: f.label,
          value: f.value,
        }))}
      />
    </DetailSection>
  );
}
